<template>
	<div
		class="style-editor-drawer app-drawer display-flex flex-column fill-height"
		:class="{ 'is-app-drawer-open': isDrawerOpen, 'is-left': isLeft }"
	>
		<div class="drawer-top-bar flex-space-between--center">
			<StepDetailsHeader :showDropdownMenu="false">
				<template #name-input>
					<div class="ml-2">{{ title }}</div>
				</template>
			</StepDetailsHeader>
		</div>
		<div class="editor-section piggyback-container bottom flex-1 flex-start--center pl-5 ml-2">
			<div v-if="btnSettings" class="editor-section-content width-100">
				<div class="edit-option mr-2" v-for="editor in EDITORS_MAP" :key="editor.text">
					<div class="edit-option-name">{{ editor.text }}</div>
					<component
						v-bind:is="editor.component"
						class="edit-option-container"
						:btnSettings.sync="btnSettings"
						:nativeEditor="isNative || isTile || isPiggyback"
						:readonly="!hasEditPermissions"
						:isPiggyback="isPiggyback || isTile"
					></component>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';

import DropdownTextStyleEditor from './ButtonEditorDrawer/tabs/DropdownTextStyleEditor';
import IconStyleEditor from './ButtonEditorDrawer/tabs/IconStyleEditor';
import ShapeStyleEditor from './ButtonEditorDrawer/tabs/ShapeStyleEditor';
import TextStyleEditor from './ButtonEditorDrawer/tabs/TextStyleEditor';
import TooltipStyleEditor from './ButtonEditorDrawer/tabs/TooltipStyleEditor';
import StepDetailsHeader from './StepDetailsHeader';
import './ButtonEditorDrawer/tabs/style-editor-layout.scss';
import { DEFAULT_INJECTION_STYLE } from '../dialogs/NewModuleDialog/ElementDataDefault.const';
import { getPlacement } from '@/store/modules/placements/getters';
import { element } from '@/store/modules/selectedWorkflow/getters';
import { mapGettersShorthand } from '@/store/utils/mappers';
import { isTileElement } from '@/utils/element-service';
import PlacementService from '@/utils/placement-service';

const EDITORS_MAP = [
	{ component: IconStyleEditor, text: 'Icon', supportNative: true },
	{ component: TextStyleEditor, text: 'Text', supportNative: true },
	{ component: ShapeStyleEditor, text: 'Shape Style', supportNative: false },
	{ component: TooltipStyleEditor, text: 'Tooltip', supportNative: false },
	{ component: DropdownTextStyleEditor, text: 'Alternate dropdown text', supportNative: false },
];

const TILE_FIELDS = [{ component: TextStyleEditor, text: 'Tile name', supportNative: true }];
const PIGGYBACK_FIELDS = [{ component: TextStyleEditor, text: 'On-send checkbox name', supportNative: true }];

export default {
	name: 'ButtonEditorDrawer',
	components: { StepDetailsHeader },
	computed: {
		...mapGettersShorthand({ element, getPlacement }),
		isDisabled() {
			return this.btnSettings
				? ['ply-element-default-icon'].includes(this.btnSettings?.icon?.name) && !this.btnSettings?.text?.value
				: true;
		},
		isTile() {
			return isTileElement(this.element.type);
		},
		EDITORS_MAP() {
			if (this.isTile) {
				return TILE_FIELDS;
			} else if (this.isPiggyback) {
				return PIGGYBACK_FIELDS;
			}
			return this.isNative ? EDITORS_MAP.filter((m) => m.supportNative) : EDITORS_MAP;
		},
		placement() {
			return this.getPlacement(this.element.placement);
		},
		isPiggyback() {
			return this.placement.is_piggyback;
		},
		title() {
			return `Display ${PlacementService.getAddOnType(this.placement, this.element.type)}`;
		},
	},
	props: {
		injectionStyle: { type: Object },
		isDrawerOpen: { type: Boolean },
		elementId: { type: [Number, String] },
		isNative: { type: Boolean },
		hasEditPermissions: { type: Boolean },
	},
	methods: {
		updateBtnSettings(value) {
			this.$set(this, 'btnSettings', value);
		},
		minimize() {
			// this.state.isDrawerOpen = !this.state.isDrawerOpen;
			this.close();
			// this.$emit('openButtonEditor', this.state.elementId);
		},
		close() {
			this.$emit('close');
		},
	},
	watch: {
		btnSettings: {
			handler(newV, oldV) {
				const hasChanged = isEqual(newV, oldV);
				if (hasChanged) return;
				this.$emit('updateElement', newV, this.elementId);
			},
			deep: true,
		},
		// injectionStyle: {
		// 	handler(newV) {
		// 		if (newV) {
		// 			this.btnSettings = cloneDeep(newV || DEFAULT_INJECTION_STYLE);
		// 		}
		// 	},
		// 	deep: true,
		// 	immediate: true,
		// },
	},
	data: () => ({
		btnSettings: null,
		isLeft: true,
		TextStyleEditor,
	}),
	mounted() {
		this.btnSettings = cloneDeep(this.injectionStyle || DEFAULT_INJECTION_STYLE);
	},
};
</script>

<style scoped lang="scss">
.style-editor-drawer {
	$side-pad: 0px;
	--secondary-text-color: #777a8d;
	--third-text-color: #515a70;
	--primary-border: 1px solid var(--primary-border-color);
	--primary-border-color: #e4e8f2;
	--border-radius: 8px;
	--darker-bg-color: #fafcff;
	--disabled-color: #eff2f9;
	--seconday-color: #5c8df5;
	--shadow-color: #5d90f4;
	--input-text-color: var(--third-text-color);
	height: 100%;
	width: 100%;

	.drawer-top-bar {
		font-size: 14px;

		.close-x:after {
			font-size: 24px;
			line-height: 19px;
		}
	}
	.title-section {
		padding: 11px $side-pad;
		font-weight: 600;
		font-size: 16px;
	}
	.editor-section {
		color: var(--third-text-color);
		height: auto;
		overflow-y: auto;

		.editor-section-content {
			height: 100%;
			overflow-y: auto;
			overflow-x: visible;

			.edit-option {
				padding: 10px $side-pad 10px $side-pad;
				&:not(:last-child) {
					border-bottom: 1px solid #eaedf5;
				}

				.edit-option-name {
					margin: 0 0 20px 0;
					font-size: 14px;
					font-weight: 500;
				}
				> :not(.edit-option-name) {
					margin: 0 0 0 15px;
				}
			}
		}

		::v-deep {
			.dropdown-section {
				margin: 0 0 20px;
			}
			.slider-container {
				width: 200px;
			}
		}
	}
	.piggyback-container {
		.editor-section-content {
			.edit-option {
				.edit-option-name {
					margin: 0.5rem 0;
				}
				> :not(.edit-option-name) {
					margin: unset;
				}
			}
		}
	}

	.drawer-top-bar {
		border-bottom: 1px solid #eaedf5;
	}
	.drawer-bottom-bar {
		padding: 10px $side-pad;
	}
}
</style>
